import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  CalendarClock,
  Plus, 
  Trash2, 
  MapPin,
  Eye,
  Loader2,
  Calendar,
  User,
  Users,
  FileText,
  Search,
  Filter,
  Building,
  Send,
  Upload,
  ZoomIn,
  DollarSign,
  Shield,
  Link2,
  FileImage,
  File,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Edit3,
  FileDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status options
const STATUS_OPTIONS = [
  { value: 'proposed', label: 'Proposed', color: 'bg-gray-500' },
  { value: 'under_review', label: 'Under Review', color: 'bg-blue-500' },
  { value: 'approved', label: 'Approved', color: 'bg-green-500' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-500' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-600' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-500' },
];

// Priority options
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'text-gray-500' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
  { value: 'high', label: 'High', color: 'text-orange-500' },
  { value: 'critical', label: 'Critical', color: 'text-red-500' },
];

// Risk level options
const RISK_LEVELS = [
  { value: 'low', label: 'Low', color: 'text-green-500' },
  { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
  { value: 'high', label: 'High', color: 'text-red-500' },
];

// Cost type options
const COST_TYPES = [
  { value: 'capex', label: 'CAPEX' },
  { value: 'opex', label: 'OPEX' },
];

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border rounded-lg">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <span className="font-medium text-sm">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {isOpen && <div className="p-3 pt-0 border-t">{children}</div>}
    </div>
  );
};

export function PlannedChangesPanel({ 
  isOpen,
  onClose,
  currentSweep,
  cameraPosition,
  pointerIntersection,
  onNavigate
}) {
  const { token } = useAuth();
  const { currentTour } = useTour();
  const [changes, setChanges] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedChange, setSelectedChange] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const fileInputRef = useRef(null);
  
  // Captured target position state
  const [capturedTargetPosition, setCapturedTargetPosition] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    building_id: '',
    status: 'proposed',
    priority: 'medium',
    proposed_start_date: '',
    proposed_end_date: '',
    change_owner_name: '',
    requested_by_name: '',
    approvers: '',
    affected_teams: '',
    contractor_vendor: '',
    operational_impact: '',
    safety_impact: '',
    compliance_impact: '',
    estimated_downtime: '',
    risk_level: 'low',
    risk_notes: '',
    mitigation_plan: '',
    estimated_cost: '',
    cost_type: '',
    budget_reference: '',
    cost_notes: '',
    prerequisites: '',
    change_rationale: '',
    capturePosition: false,
    linked_area_id: '',
    linked_area_name: '',
  });

  // Fetch changes
  const fetchChanges = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/planned-changes?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setChanges(data);
      }
    } catch (error) {
      console.error('Error fetching planned changes:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  // Fetch buildings
  const fetchBuildings = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/buildings?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setBuildings(await response.json());
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  }, [currentTour?.id, token]);

  // Fetch areas
  const fetchAreas = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/areas/tour/${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setAreas(await response.json());
    } catch (error) {
      console.error('Error fetching areas:', error);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchChanges();
      fetchBuildings();
      fetchAreas();
    }
  }, [isOpen, fetchChanges, fetchBuildings, fetchAreas]);

  // Filter changes
  const filteredChanges = changes.filter(change => {
    const matchesSearch = change.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         change.change_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || change.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !currentTour?.id) return;

    setSubmitting(true);
    try {
      const building = buildings.find(b => b.id === formData.building_id);
      
      const submitData = {
        title: formData.title,
        description: formData.description || null,
        building_id: formData.building_id || null,
        building_name: building?.name || null,
        status: formData.status,
        priority: formData.priority,
        proposed_start_date: formData.proposed_start_date || null,
        proposed_end_date: formData.proposed_end_date || null,
        change_owner_name: formData.change_owner_name || null,
        requested_by_name: formData.requested_by_name || null,
        approvers: formData.approvers ? formData.approvers.split(',').map(s => s.trim()) : [],
        affected_teams: formData.affected_teams ? formData.affected_teams.split(',').map(s => s.trim()) : [],
        contractor_vendor: formData.contractor_vendor || null,
        impact_risk: {
          operational_impact: formData.operational_impact || null,
          safety_impact: formData.safety_impact || null,
          compliance_impact: formData.compliance_impact || null,
          estimated_downtime: formData.estimated_downtime || null,
          risk_level: formData.risk_level,
          risk_notes: formData.risk_notes || null,
          mitigation_plan: formData.mitigation_plan || null,
        },
        financial: {
          estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
          cost_type: formData.cost_type || null,
          budget_reference: formData.budget_reference || null,
          cost_notes: formData.cost_notes || null,
        },
        dependencies: {
          related_workflows: [],
          related_tickets: [],
          related_risks: [],
          prerequisites: formData.prerequisites || null,
        },
        change_rationale: formData.change_rationale || null,
        tour_id: currentTour.id,
      };

      if (formData.capturePosition && currentSweep?.id) {
        submitData.sweep_id = currentSweep.id;
        if (cameraPosition?.position) submitData.position = cameraPosition.position;
        if (cameraPosition?.rotation) submitData.rotation = cameraPosition.rotation;
        // Use captured target position for accurate 3D label placement
        if (capturedTargetPosition) {
          submitData.target_position = capturedTargetPosition;
        }
      }

      // Add linked area if selected
      if (formData.linked_area_id) {
        const linkedArea = areas.find(a => a.id === formData.linked_area_id);
        submitData.linked_area_id = formData.linked_area_id;
        submitData.linked_area_name = linkedArea?.name || null;
        // If area has position and user hasn't captured a different position, use area's position
        if (!submitData.sweep_id && linkedArea?.sweep_id) {
          submitData.sweep_id = linkedArea.sweep_id;
          submitData.position = linkedArea.position;
          submitData.rotation = linkedArea.rotation;
        }
      }

      const response = await fetch(`${API_URL}/api/planned-changes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        await fetchChanges();
        handleCloseDialog();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create planned change');
      }
    } catch (error) {
      console.error('Error creating planned change:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit - populate form and open edit mode
  const handleEdit = (change) => {
    setFormData({
      title: change.title || '',
      description: change.description || '',
      building_id: change.building_id || '',
      status: change.status || 'proposed',
      priority: change.priority || 'medium',
      proposed_start_date: change.proposed_start_date || '',
      proposed_end_date: change.proposed_end_date || '',
      actual_start_date: change.actual_start_date || '',
      actual_end_date: change.actual_end_date || '',
      change_owner_name: change.change_owner_name || '',
      requested_by_name: change.requested_by_name || '',
      approvers: change.approvers?.join(', ') || '',
      affected_teams: change.affected_teams?.join(', ') || '',
      contractor_vendor: change.contractor_vendor || '',
      operational_impact: change.impact_risk?.operational_impact || '',
      safety_impact: change.impact_risk?.safety_impact || '',
      compliance_impact: change.impact_risk?.compliance_impact || '',
      estimated_downtime: change.impact_risk?.estimated_downtime || '',
      risk_level: change.impact_risk?.risk_level || 'low',
      risk_notes: change.impact_risk?.risk_notes || '',
      mitigation_plan: change.impact_risk?.mitigation_plan || '',
      estimated_cost: change.financial?.estimated_cost || '',
      cost_type: change.financial?.cost_type || '',
      budget_reference: change.financial?.budget_reference || '',
      cost_notes: change.financial?.cost_notes || '',
      prerequisites: change.dependencies?.prerequisites || '',
      change_rationale: change.change_rationale || '',
      // Keep position enabled if change already has a position
      capturePosition: !!change.sweep_id,
      linked_area_id: change.linked_area_id || '',
      linked_area_name: change.linked_area_name || '',
    });
    setIsEditMode(true);
    setIsDetailOpen(false);
    setIsCreateDialogOpen(true);
  };

  // Handle update (edit save)
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!token || !selectedChange) return;

    setSubmitting(true);
    try {
      const building = buildings.find(b => b.id === formData.building_id);
      
      const updateData = {
        title: formData.title,
        description: formData.description || null,
        building_id: formData.building_id || null,
        building_name: building?.name || null,
        status: formData.status,
        priority: formData.priority,
        proposed_start_date: formData.proposed_start_date || null,
        proposed_end_date: formData.proposed_end_date || null,
        actual_start_date: formData.actual_start_date || null,
        actual_end_date: formData.actual_end_date || null,
        change_owner_name: formData.change_owner_name || null,
        requested_by_name: formData.requested_by_name || null,
        approvers: formData.approvers ? formData.approvers.split(',').map(s => s.trim()) : [],
        affected_teams: formData.affected_teams ? formData.affected_teams.split(',').map(s => s.trim()) : [],
        contractor_vendor: formData.contractor_vendor || null,
        impact_risk: {
          operational_impact: formData.operational_impact || null,
          safety_impact: formData.safety_impact || null,
          compliance_impact: formData.compliance_impact || null,
          estimated_downtime: formData.estimated_downtime || null,
          risk_level: formData.risk_level,
          risk_notes: formData.risk_notes || null,
          mitigation_plan: formData.mitigation_plan || null,
        },
        financial: {
          estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
          cost_type: formData.cost_type || null,
          budget_reference: formData.budget_reference || null,
          cost_notes: formData.cost_notes || null,
        },
        dependencies: {
          prerequisites: formData.prerequisites || null,
        },
        change_rationale: formData.change_rationale || null,
      };

      // Handle 3D position
      if (formData.capturePosition) {
        // If we have current sweep, use the new position
        if (currentSweep?.id) {
          updateData.sweep_id = currentSweep.id;
          if (cameraPosition?.position) updateData.position = cameraPosition.position;
          if (cameraPosition?.rotation) updateData.rotation = cameraPosition.rotation;
        }
        // If no current sweep but change already has position, keep it (don't send position fields)
        // This preserves existing position when user hasn't navigated
      } else {
        // Toggle is disabled - clear position
        updateData.sweep_id = null;
        updateData.position = null;
        updateData.rotation = null;
      }

      // Handle linked area
      if (formData.linked_area_id) {
        const linkedArea = areas.find(a => a.id === formData.linked_area_id);
        updateData.linked_area_id = formData.linked_area_id;
        updateData.linked_area_name = linkedArea?.name || null;
      } else {
        updateData.linked_area_id = null;
        updateData.linked_area_name = null;
      }

      const response = await fetch(`${API_URL}/api/planned-changes/${selectedChange.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const updatedChange = await response.json();
        setChanges(prev => prev.map(c => c.id === selectedChange.id ? updatedChange : c));
        setSelectedChange(updatedChange);
        handleCloseDialog();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update planned change');
      }
    } catch (error) {
      console.error('Error updating planned change:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (changeId, newStatus) => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/api/planned-changes/${changeId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        const updatedChange = await response.json();
        setChanges(prev => prev.map(c => c.id === changeId ? updatedChange : c));
        if (selectedChange?.id === changeId) setSelectedChange(updatedChange);
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Handle add comment
  const handleAddComment = async () => {
    if (!token || !selectedChange || !newComment.trim()) return;
    try {
      const response = await fetch(
        `${API_URL}/api/planned-changes/${selectedChange.id}/activity?action=comment&details=${encodeURIComponent(newComment.trim())}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const updatedChange = await response.json();
        setSelectedChange(updatedChange);
        setChanges(prev => prev.map(c => c.id === selectedChange.id ? updatedChange : c));
        setNewComment('');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  // Handle document upload
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !token || !selectedChange) return;
    if (file.size > 15 * 1024 * 1024) { alert('File too large. Max 15MB.'); return; }

    setUploadingDoc(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      const docType = file.type.startsWith('image/') ? 'photo' : 'document';
      
      const response = await fetch(
        `${API_URL}/api/planned-changes/${selectedChange.id}/documents/upload?doc_type=${docType}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formDataObj }
      );
      if (response.ok) {
        const updatedChange = await response.json();
        setSelectedChange(updatedChange);
        setChanges(prev => prev.map(c => c.id === selectedChange.id ? updatedChange : c));
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle document delete
  const handleDeleteDoc = async (docId) => {
    if (!token || !selectedChange || !window.confirm('Delete this document?')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/planned-changes/${selectedChange.id}/documents/${docId}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const updatedDocs = selectedChange.documents.filter(d => d.id !== docId);
        const updatedChange = { ...selectedChange, documents: updatedDocs };
        setSelectedChange(updatedChange);
        setChanges(prev => prev.map(c => c.id === selectedChange.id ? updatedChange : c));
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  // Handle delete
  const handleDelete = async (changeId) => {
    if (!token || !window.confirm('Delete this planned change?')) return;
    try {
      const response = await fetch(`${API_URL}/api/planned-changes/${changeId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setChanges(prev => prev.filter(c => c.id !== changeId));
        if (selectedChange?.id === changeId) { setSelectedChange(null); setIsDetailOpen(false); }
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setIsEditMode(false);
    setCapturedTargetPosition(null);
    setFormData({
      title: '', description: '', building_id: '', status: 'proposed', priority: 'medium',
      proposed_start_date: '', proposed_end_date: '', actual_start_date: '', actual_end_date: '',
      change_owner_name: '', requested_by_name: '',
      approvers: '', affected_teams: '', contractor_vendor: '', operational_impact: '',
      safety_impact: '', compliance_impact: '', estimated_downtime: '', risk_level: 'low',
      risk_notes: '', mitigation_plan: '', estimated_cost: '', cost_type: '', budget_reference: '',
      cost_notes: '', prerequisites: '', change_rationale: '', capturePosition: false,
      linked_area_id: '', linked_area_name: '',
    });
  };

  // Download report with authentication
  const handleDownloadReport = async (changeId, changeIdDisplay) => {
    try {
      const response = await fetch(`${API_URL}/api/planned-changes/${changeId}/report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${changeIdDisplay}_report.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Failed to download report');
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(s => s.value === status);
    return option ? <Badge className={`${option.color} text-white`}>{option.label}</Badge> : null;
  };

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : null;
  const formatTimestamp = (dateStr) => dateStr ? new Date(dateStr).toLocaleString() : '';
  const formatCurrency = (amount) => amount ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount) : null;

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-[450px] sm:w-[600px] lg:w-[700px] p-0 flex flex-col">
          <SheetHeader className="p-6 pb-4 border-b flex-shrink-0">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-primary" />
              <SheetTitle>Planned Changes</SheetTitle>
            </div>
            <SheetDescription>Track and manage facility changes</SheetDescription>
          </SheetHeader>

          <div className="p-4 border-b space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search changes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-1 flex-wrap">
              {STATUS_OPTIONS.slice(0, 4).map(status => {
                const count = changes.filter(c => c.status === status.value).length;
                return (
                  <Button key={status.value} variant={statusFilter === status.value ? "default" : "outline"} size="sm" className="flex-1 text-xs px-2"
                    onClick={() => setStatusFilter(statusFilter === status.value ? 'all' : status.value)}>
                    <span className={`w-2 h-2 rounded-full ${status.color} mr-1`} />{count}
                  </Button>
                );
              })}
            </div>

            <Button onClick={() => setIsCreateDialogOpen(true)} className="w-full gap-2">
              <Plus className="h-4 w-4" />New Planned Change
            </Button>
          </div>

          <ScrollArea className="flex-1 min-h-0">
            <div className="p-4 space-y-3">
              {loading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : filteredChanges.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarClock className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No planned changes found</p>
                </div>
              ) : (
                filteredChanges.map(change => (
                  <div key={change.id} className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer"
                    onClick={() => { setSelectedChange(change); setIsDetailOpen(true); }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs font-mono text-muted-foreground">{change.change_id}</span>
                          {getStatusBadge(change.status)}
                        </div>
                        <h4 className="font-medium break-words">{change.title}</h4>
                        {change.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{change.description}</p>
                        )}
                        {change.building_name && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Building className="h-3 w-3" />{change.building_name}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                          {change.proposed_start_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(change.proposed_start_date)}</span>}
                          {change.change_owner_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{change.change_owner_name}</span>}
                          {change.sweep_id && <span className="flex items-center gap-1 text-green-600"><MapPin className="h-3 w-3" />Position saved</span>}
                        </div>
                        {change.impact_risk?.risk_level && change.impact_risk.risk_level !== 'low' && (
                          <p className={`text-xs mt-2 flex items-center gap-1 ${change.impact_risk.risk_level === 'high' ? 'text-red-500' : 'text-yellow-500'}`}>
                            <AlertTriangle className="h-3 w-3" />{change.impact_risk.risk_level.toUpperCase()} Risk
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        {change.sweep_id && onNavigate && (
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onNavigate(change.sweep_id, { rotation: change.rotation, transitionTime: 1500 }); }} title="Go to location">
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            handleDownloadReport(change.id, change.change_id);
                          }} 
                          title="Download Report"
                        >
                          <FileDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Create/Edit Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{isEditMode ? `Edit ${selectedChange?.change_id}` : 'New Planned Change'}</DialogTitle>
            <DialogDescription>{isEditMode ? 'Update the planned change details' : 'Create a new planned change for this facility'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={isEditMode ? handleUpdate : handleSubmit} className="flex-1 overflow-y-auto space-y-4 pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="title">Change Title *</Label>
                <Input id="title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="e.g., Replace Main Pump P-101" required />
              </div>
              <div className="space-y-2">
                <Label>Building</Label>
                <Select value={formData.building_id || 'none'} onValueChange={(value) => setFormData({...formData, building_id: value === 'none' ? '' : value})}>
                  <SelectTrigger><SelectValue placeholder="Select building" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Linked Area/Zone</Label>
                <Select value={formData.linked_area_id || 'none'} onValueChange={(value) => setFormData({...formData, linked_area_id: value === 'none' ? '' : value})}>
                  <SelectTrigger><SelectValue placeholder="Select area" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {areas.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {areas.length === 0 && (
                  <p className="text-xs text-muted-foreground">No areas defined. Create areas in the Areas tab.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}><span className={p.color}>{p.label}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Describe the change..." rows={2} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Proposed Start Date</Label>
                <Input type="date" value={formData.proposed_start_date} onChange={(e) => setFormData({...formData, proposed_start_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Proposed End Date</Label>
                <Input type="date" value={formData.proposed_end_date} onChange={(e) => setFormData({...formData, proposed_end_date: e.target.value})} />
              </div>
            </div>

            {isEditMode && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Actual Start Date</Label>
                  <Input type="date" value={formData.actual_start_date || ''} onChange={(e) => setFormData({...formData, actual_start_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Actual End Date</Label>
                  <Input type="date" value={formData.actual_end_date || ''} onChange={(e) => setFormData({...formData, actual_end_date: e.target.value})} />
                </div>
              </div>
            )}

            <CollapsibleSection title="People & Teams" icon={Users} defaultOpen>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Change Owner</Label>
                    <Input value={formData.change_owner_name} onChange={(e) => setFormData({...formData, change_owner_name: e.target.value})} placeholder="Name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Requested By</Label>
                    <Input value={formData.requested_by_name} onChange={(e) => setFormData({...formData, requested_by_name: e.target.value})} placeholder="Name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Approvers (comma-separated)</Label>
                  <Input value={formData.approvers} onChange={(e) => setFormData({...formData, approvers: e.target.value})} placeholder="e.g., John Smith, Jane Doe" />
                </div>
                <div className="space-y-2">
                  <Label>Affected Teams (comma-separated)</Label>
                  <Input value={formData.affected_teams} onChange={(e) => setFormData({...formData, affected_teams: e.target.value})} placeholder="e.g., Operations, Maintenance" />
                </div>
                <div className="space-y-2">
                  <Label>Contractor / Vendor</Label>
                  <Input value={formData.contractor_vendor} onChange={(e) => setFormData({...formData, contractor_vendor: e.target.value})} placeholder="If applicable" />
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Impact & Risk Assessment" icon={Shield}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Risk Level</Label>
                    <Select value={formData.risk_level} onValueChange={(value) => setFormData({...formData, risk_level: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RISK_LEVELS.map(r => <SelectItem key={r.value} value={r.value}><span className={r.color}>{r.label}</span></SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Estimated Downtime</Label>
                    <Input value={formData.estimated_downtime} onChange={(e) => setFormData({...formData, estimated_downtime: e.target.value})} placeholder="e.g., 4 hours" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Operational Impact</Label>
                  <Textarea value={formData.operational_impact} onChange={(e) => setFormData({...formData, operational_impact: e.target.value})} placeholder="How will this affect operations?" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Safety Impact</Label>
                  <Textarea value={formData.safety_impact} onChange={(e) => setFormData({...formData, safety_impact: e.target.value})} placeholder="Any safety considerations?" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Compliance Impact</Label>
                  <Textarea value={formData.compliance_impact} onChange={(e) => setFormData({...formData, compliance_impact: e.target.value})} placeholder="Regulatory/compliance implications?" rows={2} />
                </div>
                <div className="space-y-2">
                  <Label>Mitigation Plan</Label>
                  <Textarea value={formData.mitigation_plan} onChange={(e) => setFormData({...formData, mitigation_plan: e.target.value})} placeholder="How will risks be mitigated?" rows={2} />
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Financial" icon={DollarSign}>
              <div className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Estimated Cost</Label>
                    <Input type="number" value={formData.estimated_cost} onChange={(e) => setFormData({...formData, estimated_cost: e.target.value})} placeholder="0.00" />
                  </div>
                  <div className="space-y-2">
                    <Label>Cost Type</Label>
                    <Select value={formData.cost_type || 'none'} onValueChange={(value) => setFormData({...formData, cost_type: value === 'none' ? '' : value})}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {COST_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Budget Reference</Label>
                  <Input value={formData.budget_reference} onChange={(e) => setFormData({...formData, budget_reference: e.target.value})} placeholder="e.g., CAPEX-2024-Q1" />
                </div>
                <div className="space-y-2">
                  <Label>Cost Notes</Label>
                  <Textarea value={formData.cost_notes} onChange={(e) => setFormData({...formData, cost_notes: e.target.value})} placeholder="Additional cost details..." rows={2} />
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Dependencies" icon={Link2}>
              <div className="space-y-3 mt-3">
                <div className="space-y-2">
                  <Label>Prerequisites</Label>
                  <Textarea value={formData.prerequisites} onChange={(e) => setFormData({...formData, prerequisites: e.target.value})} placeholder="What must be completed before this change?" rows={2} />
                </div>
              </div>
            </CollapsibleSection>

            <div className="space-y-2">
              <Label>Change Rationale</Label>
              <Textarea value={formData.change_rationale} onChange={(e) => setFormData({...formData, change_rationale: e.target.value})} placeholder="Why is this change needed?" rows={2} />
            </div>

            <div className="p-3 rounded-lg border space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />Link to 3D Location</Label>
                <Button type="button" variant={formData.capturePosition ? "default" : "outline"} size="sm" onClick={() => {
                  setFormData(prev => ({ ...prev, capturePosition: !prev.capturePosition }));
                  if (formData.capturePosition) setCapturedTargetPosition(null);
                }}>
                  {formData.capturePosition ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              
              {formData.capturePosition && (
                <div className="space-y-2">
                  {currentSweep?.id ? (
                    <>
                      {/* Capture button */}
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant={capturedTargetPosition ? "secondary" : "outline"}
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            if (pointerIntersection?.position) {
                              setCapturedTargetPosition({...pointerIntersection.position});
                            }
                          }}
                        >
                          <MapPin className="h-4 w-4 mr-2" />
                          {capturedTargetPosition ? 'Update Location' : 'Capture Current Point'}
                        </Button>
                        {capturedTargetPosition && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setCapturedTargetPosition(null)}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      
                      {/* Status */}
                      {capturedTargetPosition ? (
                        <div className="text-xs p-2 rounded bg-green-500/10 border border-green-500/20">
                          <p className="text-green-600 font-medium">✓ Location captured!</p>
                          <p className="text-muted-foreground mt-1">
                            X: {capturedTargetPosition.x.toFixed(2)}, 
                            Y: {capturedTargetPosition.y.toFixed(2)}, 
                            Z: {capturedTargetPosition.z.toFixed(2)}
                          </p>
                        </div>
                      ) : pointerIntersection?.position ? (
                        <div className="text-xs p-2 rounded bg-blue-500/10 border border-blue-500/20">
                          <p className="text-blue-600">Point your cursor at the exact location in the 3D model, then click "Capture Current Point"</p>
                          <p className="text-muted-foreground mt-1">
                            Current: X: {pointerIntersection.position.x.toFixed(2)}, 
                            Y: {pointerIntersection.position.y.toFixed(2)}, 
                            Z: {pointerIntersection.position.z.toFixed(2)}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">Move your cursor over the 3D model to target a location</p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-yellow-600">⚠ Navigate to a location in the 3D viewer first</p>
                  )}
                </div>
              )}
              
              {!formData.capturePosition && isEditMode && selectedChange?.sweep_id && (
                <p className="text-xs text-orange-600">⚠ Saving will clear the existing position</p>
              )}
            </div>
          </form>
          <DialogFooter className="border-t pt-4 mt-2">
            <Button type="button" variant="outline" onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" disabled={submitting} onClick={(e) => {
              e.preventDefault();
              const form = e.target.closest('[role="dialog"]').querySelector('form');
              if (form) form.requestSubmit();
            }}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditMode ? 'Save Changes' : 'Create Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedChange && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="font-mono">{selectedChange.change_id}</span>
                    {getStatusBadge(selectedChange.status)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDownloadReport(selectedChange.id, selectedChange.change_id)}
                    >
                      <FileDown className="h-4 w-4 mr-2" />Report
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEdit(selectedChange)}>
                      <Edit3 className="h-4 w-4 mr-2" />Edit
                    </Button>
                  </div>
                </div>
                <DialogTitle>{selectedChange.title}</DialogTitle>
              </DialogHeader>

              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="risk">Risk & Cost</TabsTrigger>
                  <TabsTrigger value="docs">Docs ({selectedChange.documents?.length || 0})</TabsTrigger>
                  <TabsTrigger value="activity">Activity ({selectedChange.activity?.length || 0})</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4 mt-4">
                  <div className="flex items-center gap-2">
                    <Label>Status:</Label>
                    <Select value={selectedChange.status} onValueChange={(value) => handleStatusUpdate(selectedChange.id, value)}>
                      <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedChange.description && <div><Label className="text-muted-foreground">Description</Label><p className="mt-1">{selectedChange.description}</p></div>}
                  {selectedChange.change_rationale && <div><Label className="text-muted-foreground">Rationale</Label><p className="mt-1">{selectedChange.change_rationale}</p></div>}

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {selectedChange.building_name && <div><Label className="text-muted-foreground">Building</Label><p className="flex items-center gap-1"><Building className="h-4 w-4" />{selectedChange.building_name}</p></div>}
                    {selectedChange.linked_area_name && <div><Label className="text-muted-foreground">Linked Area</Label><p className="flex items-center gap-1"><MapPin className="h-4 w-4 text-primary" />{selectedChange.linked_area_name}</p></div>}
                    {selectedChange.priority && <div><Label className="text-muted-foreground">Priority</Label><p className={`capitalize ${PRIORITY_OPTIONS.find(p => p.value === selectedChange.priority)?.color}`}>{selectedChange.priority}</p></div>}
                    {selectedChange.proposed_start_date && <div><Label className="text-muted-foreground">Proposed Start</Label><p>{formatDate(selectedChange.proposed_start_date)}</p></div>}
                    {selectedChange.proposed_end_date && <div><Label className="text-muted-foreground">Proposed End</Label><p>{formatDate(selectedChange.proposed_end_date)}</p></div>}
                    {selectedChange.change_owner_name && <div><Label className="text-muted-foreground">Change Owner</Label><p>{selectedChange.change_owner_name}</p></div>}
                    {selectedChange.requested_by_name && <div><Label className="text-muted-foreground">Requested By</Label><p>{selectedChange.requested_by_name}</p></div>}
                  </div>

                  {selectedChange.approvers?.length > 0 && <div><Label className="text-muted-foreground">Approvers</Label><div className="flex flex-wrap gap-1 mt-1">{selectedChange.approvers.map((a, i) => <Badge key={i} variant="secondary">{a}</Badge>)}</div></div>}
                  {selectedChange.affected_teams?.length > 0 && <div><Label className="text-muted-foreground">Affected Teams</Label><div className="flex flex-wrap gap-1 mt-1">{selectedChange.affected_teams.map((t, i) => <Badge key={i} variant="outline">{t}</Badge>)}</div></div>}
                  {selectedChange.contractor_vendor && <div><Label className="text-muted-foreground">Contractor/Vendor</Label><p>{selectedChange.contractor_vendor}</p></div>}

                  {selectedChange.sweep_id && (
                    <Button variant="outline" className="w-full" onClick={() => { onNavigate?.(selectedChange.sweep_id, { rotation: selectedChange.rotation, transitionTime: 1500 }); setIsDetailOpen(false); onClose(); }}>
                      <MapPin className="h-4 w-4 mr-2" />Go to Location
                    </Button>
                  )}
                </TabsContent>

                <TabsContent value="risk" className="space-y-4 mt-4">
                  {selectedChange.impact_risk && (
                    <div className="space-y-3">
                      <h4 className="font-medium flex items-center gap-2"><Shield className="h-4 w-4" />Impact & Risk</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><Label className="text-muted-foreground">Risk Level</Label><p className={`capitalize ${RISK_LEVELS.find(r => r.value === selectedChange.impact_risk.risk_level)?.color}`}>{selectedChange.impact_risk.risk_level || 'Not assessed'}</p></div>
                        {selectedChange.impact_risk.estimated_downtime && <div><Label className="text-muted-foreground">Est. Downtime</Label><p>{selectedChange.impact_risk.estimated_downtime}</p></div>}
                      </div>
                      {selectedChange.impact_risk.operational_impact && <div><Label className="text-muted-foreground">Operational Impact</Label><p className="mt-1 text-sm">{selectedChange.impact_risk.operational_impact}</p></div>}
                      {selectedChange.impact_risk.safety_impact && <div><Label className="text-muted-foreground">Safety Impact</Label><p className="mt-1 text-sm">{selectedChange.impact_risk.safety_impact}</p></div>}
                      {selectedChange.impact_risk.compliance_impact && <div><Label className="text-muted-foreground">Compliance Impact</Label><p className="mt-1 text-sm">{selectedChange.impact_risk.compliance_impact}</p></div>}
                      {selectedChange.impact_risk.mitigation_plan && <div><Label className="text-muted-foreground">Mitigation Plan</Label><p className="mt-1 text-sm">{selectedChange.impact_risk.mitigation_plan}</p></div>}
                    </div>
                  )}

                  {selectedChange.financial && (
                    <div className="space-y-3 pt-4 border-t">
                      <h4 className="font-medium flex items-center gap-2"><DollarSign className="h-4 w-4" />Financial</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {selectedChange.financial.estimated_cost && <div><Label className="text-muted-foreground">Estimated Cost</Label><p className="font-medium">{formatCurrency(selectedChange.financial.estimated_cost)}</p></div>}
                        {selectedChange.financial.cost_type && <div><Label className="text-muted-foreground">Cost Type</Label><p className="uppercase">{selectedChange.financial.cost_type}</p></div>}
                        {selectedChange.financial.budget_reference && <div><Label className="text-muted-foreground">Budget Reference</Label><p>{selectedChange.financial.budget_reference}</p></div>}
                      </div>
                      {selectedChange.financial.cost_notes && <div><Label className="text-muted-foreground">Cost Notes</Label><p className="mt-1 text-sm">{selectedChange.financial.cost_notes}</p></div>}
                    </div>
                  )}

                  {selectedChange.dependencies?.prerequisites && (
                    <div className="space-y-3 pt-4 border-t">
                      <h4 className="font-medium flex items-center gap-2"><Link2 className="h-4 w-4" />Dependencies</h4>
                      <div><Label className="text-muted-foreground">Prerequisites</Label><p className="mt-1 text-sm">{selectedChange.dependencies.prerequisites}</p></div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="docs" className="mt-4">
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <input type="file" ref={fileInputRef} onChange={handleDocUpload} className="hidden" />
                      <Button onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc} className="w-full">
                        {uploadingDoc ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="h-4 w-4 mr-2" />Upload Document</>}
                      </Button>
                    </div>

                    {selectedChange.documents?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedChange.documents.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border">
                            <div className="flex items-center gap-3">
                              {doc.doc_type === 'photo' ? <FileImage className="h-5 w-5 text-blue-500" /> : <File className="h-5 w-5 text-gray-500" />}
                              <div>
                                <p className="font-medium text-sm">{doc.name}</p>
                                <p className="text-xs text-muted-foreground">{doc.uploaded_by_name} • {formatTimestamp(doc.uploaded_at)}</p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {doc.url.startsWith('data:image') && <Button variant="ghost" size="icon" onClick={() => setSelectedDoc(doc)}><ZoomIn className="h-4 w-4" /></Button>}
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteDoc(doc.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground"><FileText className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No documents yet</p></div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input placeholder="Add a comment..." value={newComment} onChange={(e) => setNewComment(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddComment()} />
                      <Button onClick={handleAddComment} disabled={!newComment.trim()}><Send className="h-4 w-4" /></Button>
                    </div>

                    <ScrollArea className="h-[300px]">
                      <div className="space-y-3">
                        {selectedChange.activity?.slice().reverse().map(activity => (
                          <div key={activity.id} className="p-3 rounded-lg bg-muted/50">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-sm">{activity.user_name}</span>
                              <span className="text-xs text-muted-foreground">{formatTimestamp(activity.timestamp)}</span>
                            </div>
                            <p className="text-sm">
                              {activity.action === 'comment' ? activity.details : activity.action === 'status_changed' ? (
                                <span>Changed status from <Badge variant="outline">{activity.old_value}</Badge> to <Badge variant="outline">{activity.new_value}</Badge></span>
                              ) : (activity.details || activity.action.replace(/_/g, ' '))}
                            </p>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedChange.id)}><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="max-w-3xl p-0">
          {selectedDoc && (
            <div className="relative">
              <img src={selectedDoc.url} alt={selectedDoc.name} className="w-full max-h-[80vh] object-contain" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <p className="text-white font-medium">{selectedDoc.name}</p>
                <p className="text-white/70 text-sm">Uploaded by {selectedDoc.uploaded_by_name} • {formatTimestamp(selectedDoc.uploaded_at)}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PlannedChangesPanel;
